import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FiRefreshCw, FiEye, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import mailManagementService from '../../services/mailManagementService';
import { useProject } from '../../context/ProjectContext';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import { Modal } from '../Common/Modal';
import AuditDetailModal from '../Audit/AuditDetailModal';
import Pagination from '../Common/Pagination';
import '../Common/DataGrid.css';
import './MailManagement.css';

const SUBMODULES = [
  { key: 'Visit Letter', label: 'Visit Letter' },
  { key: 'PI Confirmation', label: 'PI Confirmation' },
];

const SORT_KEYS = {
  visitId: 'vVisitId',
  visitType: 'vVisitType',
  mailStatus: 'bMailStatus',
  recordedOn: 'dRecordedOnUTC',
};

const MailManagement = () => {
  const { currentProject } = useProject();
  const projectCode = currentProject && currentProject !== 'all'
    ? (typeof currentProject === 'string' ? currentProject : (currentProject?.projectCode ?? currentProject?.ProjectCode ?? ''))
    : null;
  const [activeTab, setActiveTab] = useState('Visit Letter');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [letterModal, setLetterModal] = useState({ open: false, data: null, loading: false });
  const [auditModal, setAuditModal] = useState({ open: false, recordId: null, onDate: null });
  const [resendingId, setResendingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recordedOn');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const letterContentRef = useRef(null);

  const isVisitActionLink = (anchorEl) => {
    if (!anchorEl) return false;
    const text = (anchorEl.textContent ?? '').trim().toLowerCase();
    return text === 'approve' || text === 'reject';
  };

  const disableVisitActionLinks = () => {
    const root = letterContentRef.current;
    if (!root) return;

    root.querySelectorAll('a').forEach((anchorEl) => {
      if (!isVisitActionLink(anchorEl)) return;

      if (!anchorEl.dataset.disabledHref && anchorEl.getAttribute('href')) {
        anchorEl.dataset.disabledHref = anchorEl.getAttribute('href');
      }
      anchorEl.removeAttribute('href');
      anchorEl.setAttribute('tabindex', '-1');
      anchorEl.setAttribute('aria-disabled', 'true');
      anchorEl.classList.add('mail-link-disabled');
    });
  };

  const fetchList = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mailManagementService.getList(activeTab, projectCode ?? undefined);
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load mail history');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [activeTab, projectCode]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    disableVisitActionLinks();
  }, [letterModal.open, letterModal.loading, letterModal.data]);

  const mailStatusDisplay = (bMailStatus) =>
    bMailStatus ? 'Sent' : 'Error while sending the mail!';

  const filteredAndSorted = useMemo(() => {
    const searchLower = searchTerm.trim().toLowerCase();
    let result = list;
    if (searchLower) {
      result = result.filter((row) => {
        const visitId = (row.vVisitId ?? row.nScheduledVisitID ?? '').toString();
        const visitType = (row.vVisitType ?? '').toString();
        const status = mailStatusDisplay(row.bMailStatus);
        const text = [visitId, visitType, status].join(' ').toLowerCase();
        return text.includes(searchLower);
      });
    }
    const key = SORT_KEYS[sortBy] || SORT_KEYS.recordedOn;
    result = [...result].sort((a, b) => {
      let va = a[key];
      let vb = b[key];
      if (key === 'dRecordedOnUTC') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
        return sortOrder === 'asc' ? va - vb : vb - va;
      }
      if (key === 'bMailStatus') {
        va = va ? 1 : 0;
        vb = vb ? 1 : 0;
        return sortOrder === 'asc' ? va - vb : vb - va;
      }
      va = (va ?? '').toString();
      vb = (vb ?? '').toString();
      const cmp = va.localeCompare(vb, undefined, { numeric: true });
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [list, searchTerm, sortBy, sortOrder]);

  const totalItems = filteredAndSorted.length;
  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAndSorted.slice(start, start + pageSize);
  }, [filteredAndSorted, page, pageSize]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder(column === 'recordedOn' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />;
  };

  const handleViewContent = async (mailRecordId) => {
    if (!mailRecordId) return;
    setLetterModal({ open: true, data: null, loading: true });
    try {
      const data = await mailManagementService.getMailContent(mailRecordId);
      setLetterModal((prev) => ({ ...prev, data, loading: false }));
    } catch (err) {
      setLetterModal((prev) => ({ ...prev, data: null, loading: false }));
      setToast({ type: 'error', message: err?.response?.data?.error || 'Failed to load mail content.' });
    }
  };

  const handleResend = async (id) => {
    setResendingId(id);
    try {
      const result = await mailManagementService.resend(id);
      setToast({ type: 'success', message: result?.message || 'Resend completed.' });
      fetchList();
    } catch (err) {
      setToast({ type: 'error', message: err?.response?.data?.error || 'Resend failed.' });
    } finally {
      setResendingId(null);
    }
  };

  const openAudit = (recordId, onDate) => {
    setAuditModal({ open: true, recordId, onDate: onDate || null });
  };

  return (
    <div className="mail-management-page">
      {toast && (
        <div style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 9999 }}>
          <Alert
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        </div>
      )}
      {error && <Alert type="error" message={error} />}

      <div className="mail-management-tabs">
        {SUBMODULES.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`tab-button ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="mail-management-grid-wrap">
          {loading ? (
            <Spinner />
          ) : (
            <>
              <div className="data-grid-toolbar">
                <div className="data-grid-controls">
                  <div className="data-grid-search">
                    <input
                      type="text"
                      className="form-input data-grid-search-input"
                      placeholder="Search Visit ID, Type, Status..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="data-grid-filter">
                    <span className="data-grid-filter-label">Page size</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="form-select data-grid-filter-select data-grid-page-size"
                    >
                      <option value={5}>5 items</option>
                      <option value={10}>10 items</option>
                      <option value={25}>25 items</option>
                      <option value={50}>50 items</option>
                      <option value={100}>100 items</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="data-grid-table-wrapper table-responsive">
                <table className="mail-management-table">
                  <thead>
                    <tr>
                      <th>
                        <button type="button" className="mail-management-th-sort" onClick={() => handleSort('visitId')}>
                          Visit ID <SortIcon column="visitId" />
                        </button>
                      </th>
                      <th>
                        <button type="button" className="mail-management-th-sort" onClick={() => handleSort('visitType')}>
                          Visit Type <SortIcon column="visitType" />
                        </button>
                      </th>
                      <th>
                        <button type="button" className="mail-management-th-sort" onClick={() => handleSort('mailStatus')}>
                          Mail Status <SortIcon column="mailStatus" />
                        </button>
                      </th>
                      <th>Visit Letter Detail</th>
                      <th className="text-center">Action</th>
                      <th className="text-center">Audit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="empty-state">
                          {list.length === 0 ? 'No records found.' : 'No matches. Try a different search.'}
                        </td>
                      </tr>
                    ) : (
                      pageRows.map((row) => (
                      <tr key={row.nMailManagementMstNo}>
                        <td className="font-bold">{row.vVisitId ?? row.nScheduledVisitID ?? '—'}</td>
                        <td>{row.vVisitType ?? '—'}</td>
                        <td>
                          <span
                            className={
                              row.bMailStatus
                                ? 'mail-status sent'
                                : 'mail-status error'
                            }
                          >
                            {mailStatusDisplay(row.bMailStatus)}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() =>
                              handleViewContent(row.nMailManagementMstNo ?? row.id)
                            }
                            title="View stored mail content"
                          >
                            <FiEye size={16} /> View
                          </button>
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => handleResend(row.nMailManagementMstNo ?? row.id)}
                            disabled={resendingId === (row.nMailManagementMstNo ?? row.id)}
                          >
                            <FiRefreshCw
                              size={16}
                              className={
                                resendingId === (row.nMailManagementMstNo ?? row.id)
                                  ? 'spin'
                                  : ''
                              }
                            />{' '}
                            Resend Mail
                          </button>
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() =>
                              openAudit(row.nMailManagementMstNo ?? row.id, row.dRecordedOnUTC)
                            }
                          >
                            Audit
                          </button>
                        </td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={page}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                showPageSize={false}
              />
            </>
          )}
        </div>
      </div>

      {/* Visit Letter Detail Modal */}
      <Modal
        isOpen={letterModal.open}
        onClose={() => setLetterModal({ open: false, data: null, loading: false })}
        title="Visit Letter Detail"
        size="large"
        className="visit-letter-detail-modal"
        footer={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() =>
              setLetterModal({ open: false, data: null, loading: false })
            }
          >
            Close
          </button>
        }
        showCloseButton
      >
        <div className="visit-letter-detail-body">
          {letterModal.loading && <Spinner />}
          {!letterModal.loading && letterModal.data != null && (
            <div className="letter-content">
              {letterModal.data.rawMailContent ? (
                <div
                  ref={letterContentRef}
                  className="letter-section letter-raw-content"
                  onClickCapture={(e) => {
                    const targetLink = e.target.closest('a');
                    if (!isVisitActionLink(targetLink)) return;
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onKeyDownCapture={(e) => {
                    const targetLink = e.target.closest('a');
                    if (!isVisitActionLink(targetLink)) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  dangerouslySetInnerHTML={{
                    __html: letterModal.data.rawMailContent,
                  }}
                />
              ) : (
                <p className="text-muted">No content stored for this record.</p>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Audit Modal */}
      <AuditDetailModal
        isOpen={auditModal.open}
        onClose={() => setAuditModal({ open: false, recordId: null, onDate: null })}
        tableName="MailManagementMst"
        recordId={auditModal.recordId}
        onDate={auditModal.onDate}
        title="Field Audit - Mail Management"
        displayMode="table"
      />
    </div>
  );
};

export default MailManagement;
